import { useEffect, useState } from "react";
import { Link2, Copy, ExternalLink, RefreshCw, Sparkles, BarChart3, MousePointerClick } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { SimpleSelect } from "@/components/wizard/Field";
import { t, fmtDate } from "@/i18n";

export const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); toast.success(t("Enlace copiado")); } catch { toast.error(t("No se pudo copiar")); }
};

export const SmartlinkCard = ({ release, onUpdated }) => {
  const { can } = useAuth();
  const staff = can("admin", "manager");
  const link = release.smartlink;
  const [domains, setDomains] = useState([]);
  const [domain, setDomain] = useState("__default__");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (staff) api.get("/smartlinks/domains").then((r) => setDomains(r.data)).catch(() => {}); }, [staff]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/releases/${release.id}/smartlink`, { domain: domain === "__default__" ? null : domain });
      toast.success(t(link ? "Smartlink regenerado" : "Smartlink creado"), { description: data.url });
      onUpdated?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <section className="panel p-5 sm:p-6 space-y-4" data-testid="smartlink-card">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-md bg-blue-500/10 text-blue-400 grid place-items-center shrink-0"><Link2 className="h-4 w-4" /></div>
        <div className="min-w-0">
          <h3 className="font-display font-semibold">Smartlink</h3>
          <p className="text-xs text-muted-foreground">{t(link ? (link.type === "presave" ? "Pre-save activo · se convierte en página de lanzamiento el día del estreno" : "Página de lanzamiento con todas las plataformas") : "Un solo enlace para compartir con tus fans en todas las plataformas.")}</p>
        </div>
      </div>
      {link ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2 rounded-md border border-border bg-secondary/40 px-3 py-2">
            <a href={link.url} target="_blank" rel="noreferrer" className="font-mono text-sm text-primary truncate flex-1 hover:underline" data-testid="smartlink-url">{link.url}</a>
            <button onClick={() => copyText(link.url)} className="text-muted-foreground hover:text-foreground" title={t("Copiar")} data-testid="smartlink-copy"><Copy className="h-4 w-4" /></button>
            <a href={link.url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground" title={t("Abrir")} data-testid="smartlink-open"><ExternalLink className="h-4 w-4" /></a>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            <span data-testid="smartlink-type" className="rounded-full border border-border px-2 py-0.5">{t(link.type === "presave" ? "Pre-save" : "Lanzamiento")}</span>
            <span>{t("Creado {d}", { d: fmtDate(link.created_at) })}</span>
            {link.clicks !== undefined && <span className="flex items-center gap-1 text-foreground" data-testid="smartlink-clicks"><MousePointerClick className="h-3 w-3" /> {t("{a} clics · {b} únicos", { a: link.clicks, b: link.unique_clicks })}</span>}
            {staff && link.metrics_url && <a href={link.metrics_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-foreground" data-testid="smartlink-metrics"><BarChart3 className="h-3 w-3" /> {t("Métricas")}</a>}
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="smartlink-empty">{t(release.upc ? (staff ? "Aún no hay smartlink para este lanzamiento." : "Tu enlace aparecerá aquí en cuanto el equipo lo genere.") : "Disponible cuando el lanzamiento tenga UPC.")}</p>
      )}
      {release.smartlink_error && staff && <p className="text-xs text-destructive" data-testid="smartlink-error">{t("Último intento automático: {e}", { e: release.smartlink_error })}</p>}
      {staff && release.upc && (
        <div className="flex flex-wrap items-end gap-2 pt-1">
          <div className="w-56">
            <SimpleSelect value={domain} onChange={setDomain} options={[["__default__", t("Dominio según la cuenta del artista")], ...domains.map((d) => [d.domain, d.domain])]} testId="smartlink-domain-select" />
          </div>
          <Button size="sm" variant={link ? "outline" : "default"} onClick={create} disabled={busy} className="gap-1.5 h-9" data-testid="smartlink-create-button">
            {link ? <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> : <Sparkles className="h-3.5 w-3.5" />} {t(busy ? "Creando…" : link ? "Regenerar" : "Crear smartlink")}
          </Button>
        </div>
      )}
    </section>
  );
};
